'use client';

import { type ReactNode, useEffect, useState } from 'react';

import { Button, type ButtonProps, type ButtonVariant } from '@commons/components/Button';
import Drawer, { type DrawerProps } from '@commons/components/Drawer';
import { replaceURLSearchParam } from '@commons/helpers/url';

const FORM_QUERY_PARAM = 'form';

// Type definition for the WebformDrawer component properties.

export type WebformDrawerProps = Omit<DrawerProps, 'trigger' | 'triggerProps'> & {
  webformId?: string;
  trigger?: ReactNode;
  triggerProps?: Omit<ButtonProps, 'variant'> & {
    label: string;
    variant?: ButtonVariant; // It's required by default, but we make it optional here.
  };
};

/**
 * Webform drawer component.
 *
 * Renders a trigger that opens a drawer containing a webform.
 */

const WebformDrawer = (props: WebformDrawerProps) => {
  const { theme, children, trigger, triggerProps, webformId, open, onOpenChange, ...rest } = props;

  const [isOpen, setIsOpen] = useState<boolean>();

  // Handle the drawer open state change.
  const handleOpenChange = (value: boolean) => {
    if (webformId) {
      replaceURLSearchParam(FORM_QUERY_PARAM, value ? webformId : null);
    }
    setIsOpen(value);
    onOpenChange?.(value);
  };

  // Check if the webform ID in the URL matches the provided webform ID and open the drawer if they match.
  useEffect(() => {
    if (!webformId) return;

    const formId = new URLSearchParams(window.location.search).get(FORM_QUERY_PARAM);
    if (formId !== webformId) return;

    replaceURLSearchParam(FORM_QUERY_PARAM, null);
    setIsOpen(true);
    onOpenChange?.(true);
  }, [onOpenChange, webformId]);

  let renderedButtonTrigger = null;

  if (triggerProps) {
    const { label, variant = 'secondary', ...btnProps } = triggerProps;
    renderedButtonTrigger = (
      <Button variant={variant} {...btnProps}>
        {label}
      </Button>
    );
  }

  return (
    <Drawer
      theme={theme}
      trigger={trigger ?? renderedButtonTrigger}
      {...rest}
      open={isOpen ?? open}
      onOpenChange={handleOpenChange}
    >
      {children}
    </Drawer>
  );
};

export default WebformDrawer;
